/**
 * Ask RunAI Window — main AI interaction window
 * Perplexity-style: input + skill pills + message list
 * Sends to Anne Filiz (agent_id=1) via /api/chat/send
 */
import { Send, Trash2, Zap } from 'lucide-react'
import { useRef, useState } from 'react'
import MessageList from './MessageList'
import SkillPills from './SkillPills'
import { useAskRunAI } from './useAskRunAI'

export default function AskRunAIWindow() {
  const [input, setInput] = useState('')
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const { messages, loading, send, clear } = useAskRunAI()

  const handleSend = () => {
    const text = input.trim()
    if (!text || loading) { return }
    setInput('')
    void send(text)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleSkillSelect = (text: string) => {
    setInput(text)
    inputRef.current?.focus()
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: 'transparent',
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 20px 12px',
        borderBottom: '1px solid var(--os-border)',
        background: 'linear-gradient(180deg,rgba(99,102,241,0.04) 0%,transparent 100%)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: 800, background: 'linear-gradient(135deg,#6366f1,#ec4899)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Ask RunAI
            </div>
            <div style={{ fontSize: '10px', color: 'var(--os-dim)', marginTop: '2px' }}>
              512 agents · 89 skills · Multi-cloud · Live
            </div>
          </div>
          {messages.length > 0 && (
            <button
              onClick={clear}
              title="Clear conversation"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--os-dim)', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', padding: '4px 8px', borderRadius: '6px' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; e.currentTarget.style.color = '#ef4444' }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--os-dim)' }}
            >
              <Trash2 size={12} /> Clear
            </button>
          )}
        </div>

        {/* Input */}
        <div style={{ position: 'relative' }}>
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Analyze competitor websites, create a report, write code..."
            rows={2}
            style={{
              width: '100%',
              padding: '10px 44px 10px 14px',
              background: 'var(--os-input-bg)',
              border: '1.5px solid var(--os-border)',
              borderRadius: '10px',
              color: 'var(--os-text)',
              fontSize: '12px',
              outline: 'none',
              resize: 'none',
              fontFamily: 'inherit',
              lineHeight: '1.5',
              transition: 'border 0.2s',
              boxSizing: 'border-box',
            }}
            onFocus={e => { e.target.style.borderColor = 'rgba(99,102,241,0.5)' }}
            onBlur={e => { e.target.style.borderColor = 'var(--os-border)' }}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || loading}
            style={{
              position: 'absolute',
              right: '8px',
              bottom: '8px',
              width: '28px',
              height: '28px',
              borderRadius: '7px',
              background: input.trim() && !loading ? '#6366f1' : 'rgba(255,255,255,0.04)',
              border: 'none',
              cursor: input.trim() && !loading ? 'pointer' : 'default',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: input.trim() && !loading ? '#fff' : 'var(--os-dim)',
              transition: 'all 0.15s',
            }}
          >
            {loading
              ? <Zap size={13} style={{ animation: 'spin 1s linear infinite' }} />
              : <Send size={13} />
            }
          </button>
        </div>

        <SkillPills onSelect={handleSkillSelect} />
        <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      </div>

      {/* Messages */}
      <MessageList messages={messages} loading={loading} />
    </div>
  )
}
